// Recently-viewed visits — localStorage-backed, read by the command palette.
//
// Tracks the last N patients the clinician opened in /clinic/<uuid>. The
// command palette surfaces these as a "Recent" section when the search
// box is empty, so power users can rebound to the last 5 visits with two
// keystrokes (⌘K → Enter on the row).
//
// Why localStorage: zero backend cost, persists across sessions on the
// same device, and the whole feature degrades gracefully if storage is
// unavailable (private mode, quota errors, etc.).
//
// Storage key: vita-recent-visits
// Shape: JSON array of { uuid, name, time, lastSeen } — newest first.

import { useEffect } from "react";

const STORAGE_KEY = "vita-recent-visits";
const MAX_ENTRIES = 10;

function safeGet() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (_) {
    return [];
  }
}

function safeSet(arr) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
  } catch (_) {
    // quota or private mode — swallow.
  }
}

/**
 * Push a visit to the front of the recents list, dedupe by uuid, cap at
 * MAX_ENTRIES. No-op if uuid or name is falsy.
 */
export function recordVisit({ uuid, name, time }) {
  if (!uuid || !name) return;
  const now = Date.now();
  const existing = safeGet().filter((v) => v && v.uuid !== uuid);
  const next = [{ uuid, name, time: time || "", lastSeen: now }, ...existing].slice(0, MAX_ENTRIES);
  safeSet(next);
}

/**
 * Return the recent-visits list, newest first. Optional limit.
 */
export function getRecentVisits(limit = MAX_ENTRIES) {
  return safeGet().slice(0, limit);
}

/** Clear all recents — exposed for a future "clear history" command. */
export function clearRecentVisits() {
  safeSet([]);
}

/**
 * Hook: call from a page that knows what visit is currently being
 * viewed, and the visit will be recorded as soon as both uuid and name
 * are available. Safe to call with undefined values during loading —
 * it'll no-op until the data arrives.
 */
export function useRecordVisit(uuid, name, time) {
  useEffect(() => {
    if (!uuid || !name) return;
    recordVisit({ uuid, name, time });
  }, [uuid, name, time]);
}
